import {useCallback, useContext, useReducer, useRef} from "react";
import {useNavigate} from "react-router-dom";
import API from "../logic/API";
import {AppAvailabilityStatus} from "../logic/APITypes";
import {statusReducer} from "../utility/statusReducer";
import {SnackbarContext} from "../utility/SnackbarProvider";
import {useIntl} from "react-intl";

/** How often to poll the status endpoint (the backend caches the helm status for ~10s) */
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 20 * 60 * 1000;
const NOT_FOUND_FAILURE_LIMIT = 5;

/** A hook for handling all actions regarding apps (currently install, cancel, uninstall, getStatus) */
function useAppActions(groupId: string) {
  const [loadingStates, dispatch] = useReducer(statusReducer, {});
  const abortControllers = useRef<Record<string, AbortController>>({});
  const pollingIntervals = useRef<Record<string, NodeJS.Timeout>>({});
  const navigate = useNavigate();
  const {addSnackbar} = useContext(SnackbarContext);
  const intl = useIntl();

  const navigateToGroup = useCallback(() => {
    if (window.location.pathname === `/groups/${groupId}`) {
      window.location.reload();
    } else {
      navigate(`/groups/${groupId}`);
    }
  }, [navigate]);

  const pollForStatus = useCallback(
    async (appId: string, targetStatus: AppAvailabilityStatus) => {
      let errorCount = 0;
      let notFoundCount = 0;
      let elapsedMs = 0;
      return new Promise<void>((resolve) => {
        const cleanup = () => {
          dispatch({type: "STOP_LOADING", appId});
          clearInterval(pollingIntervals.current[appId]);
          delete pollingIntervals.current[appId];
          resolve();
        };
        const fail = () => {
          cleanup();
          if (targetStatus === "available") {
            addSnackbar({
              variant: "close",
              text: intl.formatMessage({
                id: "appDetails.snackbar.install.error",
              }),
              hasCloseButton: true,
            });
          }
        };
        const poll = async () => {
          elapsedMs += POLL_INTERVAL_MS;
          if (elapsedMs >= MAX_POLL_DURATION_MS) {
            fail();
            return;
          }
          try {
            API.getAppStatus(
              groupId,
              appId,
              targetStatus === "unavailable",
            ).then((res) => {
              if (res.success && res.payload) {
                errorCount = 0;
                if (res.payload.available === targetStatus) {
                  cleanup();
                  navigateToGroup();
                  return;
                }
                if (targetStatus === "available") {
                  if (res.payload.installation_status === "failed") {
                    fail();
                    return;
                  }
                  notFoundCount =
                    res.payload.installation_status === "not-found"
                      ? notFoundCount + 1
                      : 0;
                  if (notFoundCount >= NOT_FOUND_FAILURE_LIMIT) {
                    fail();
                  }
                }
              } else {
                errorCount++;
                if (errorCount >= 5) {
                  cleanup();
                }
              }
            });
          } catch (error) {
            console.error(`Error while polling status for ${appId}:`, error);
            errorCount++;
            if (errorCount >= 5) {
              cleanup();
              return;
            }
          }
        };

        pollingIntervals.current[appId] = setInterval(poll, POLL_INTERVAL_MS);
      });
    },
    [groupId, navigateToGroup, addSnackbar, intl],
  );

  const installApp = useCallback(
    async (appId: string, parameters?: object) => {
      dispatch({type: "START_LOADING", appId});

      if (!abortControllers.current[appId]) {
        abortControllers.current[appId] = new AbortController();
      }
      API.installApp(
        groupId,
        appId,
        abortControllers.current[appId],
        parameters,
      ).then(async (res) => {
        if (res.success) {
          await pollForStatus(appId, "available");
        } else {
          dispatch({type: "STOP_LOADING", appId});
          addSnackbar({
            variant: "close",
            text: intl.formatMessage({
              id: "appDetails.snackbar.install.error",
            }),
            hasCloseButton: true,
          });
        }
      });
    },
    [abortControllers, pollForStatus, navigate],
  );

  const cancelInstall = useCallback(
    async (appId: string) => {
      if (abortControllers.current[appId]) {
        abortControllers.current[appId].abort();
        abortControllers.current[appId] = new AbortController();
      }

      if (pollingIntervals.current[appId]) {
        clearInterval(pollingIntervals.current[appId]);
        delete pollingIntervals.current[appId];
      }

      dispatch({type: "STOP_LOADING", appId});
      await API.cancelInstallApp(groupId, appId);
    },
    [abortControllers, pollingIntervals],
  );

  const uninstallApp = useCallback(
    async (appId: string) => {
      dispatch({type: "START_LOADING", appId});
      API.uninstallApp(groupId, appId).then(async (res) => {
        if (res.success && res.payload) {
          await pollForStatus(appId, "unavailable");
        }
      });
    },
    [abortControllers, pollForStatus, navigate],
  );

  return {
    loadingStates,
    installApp,
    cancelInstall,
    uninstallApp,
    pollForStatus,
  };
}

export default useAppActions;
